import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { SITE, HERO_IMAGES } from "@/data/site";
import { useAbout, useSiteSettings } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const FALLBACK_GALLERY = [HERO_IMAGES.map, HERO_IMAGES.manuscript, HERO_IMAGES.astronomy, HERO_IMAGES.primary];

export default function About() {
  const about = useAbout();
  const settings = useSiteSettings();
  const founder = settings?.founder_name || SITE.founder;
  useDocumentTitle(`About ${founder}`);

  if (!about) {
    return <div className="container-wide py-24 font-heading text-2xl" data-testid="about-loading">Loading…</div>;
  }

  const portrait = about.portrait_url ? mediaUrl(about.portrait_url) : null;
  const roleLine = about.role_line;
  const bio = about.bio;
  const bioExtended = about.bio_extended;
  const gallery = (about.gallery?.images || []).filter(Boolean);
  const galleryToRender = gallery.length ? gallery : FALLBACK_GALLERY;

  return (
    <div data-testid="about-page">
      <section className="container-wide pt-16 pb-16 grid gap-16 lg:grid-cols-[1fr_1.2fr] items-start">
        <div>
          <div className="aspect-[4/5] border overflow-hidden" style={{ borderColor: "var(--gi-border)", background: "var(--gi-sandstone)" }} data-testid="about-portrait">
            {portrait ? (
              <img src={portrait} alt={`Portrait of ${founder}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
                <div className="font-heading text-3xl" style={{ color: "var(--gi-maroon)" }}>{founder}</div>
                <div className="mt-2 text-xs" style={{ color: "var(--gi-muted)" }}>{roleLine}</div>
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }} data-testid="about-eyebrow">{about.eyebrow}</div>
          <h1 className="mt-3 font-heading text-5xl sm:text-6xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }} data-testid="about-heading">{founder}</h1>
          <div className="mt-3 font-accent text-xs" style={{ color: "var(--gi-muted)", letterSpacing: "0.14em" }} data-testid="about-role-line">{roleLine}</div>
          <div className="hair-divider my-8 max-w-xs" />
          <p className="text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }} data-testid="about-bio">
            {bio}
          </p>
          {bioExtended && (
            <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }} data-testid="about-bio-extended">
              {bioExtended}
            </p>
          )}
        </div>
      </section>

      <Section eyebrow={about.journey?.eyebrow} title={about.journey?.title}>
        <div className="grid gap-10 md:grid-cols-2">
          {(about.journey?.nodes || []).filter(n => (n || "").trim()).map((t, i) => (
            <div key={`journey-${i}`} className="pillar" data-testid={`about-journey-${i}`}>
              <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Node {String(i + 1).padStart(2, "0")}</div>
              <p className="mt-2 text-base" style={{ color: "var(--gi-charcoal-2)" }}>{t}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={about.bibliography?.eyebrow} title={about.bibliography?.title} background>
        <p className="text-base" style={{ color: "var(--gi-charcoal-2)" }} data-testid="about-biblio-body">
          {about.bibliography?.body}
        </p>
        {about.bibliography?.cta_label && (
          <Link to={about.bibliography?.cta_to || "/books"} className="btn-maroon mt-6" data-testid="about-books-btn">
            {about.bibliography.cta_label} <ArrowRight size={14} />
          </Link>
        )}
      </Section>

      <Section eyebrow={about.environmental?.eyebrow} title={about.environmental?.title}>
        <div className="grid gap-8 md:grid-cols-3">
          {(about.environmental?.orgs || []).filter(o => (o?.title || "").trim()).map((c, i) => {
            const inner = (
              <>
                <div className="font-heading text-xl" style={{ color: "var(--gi-maroon)" }}>{c.title}</div>
                <p className="mt-3 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>{c.body}</p>
                {c.url && (
                  <div className="mt-4 font-accent text-[10px] flex items-center gap-2" style={{ color: "var(--gi-maroon)" }}>
                    Visit <ExternalLink size={11} />
                  </div>
                )}
              </>
            );
            const testId = `about-org-${(c.title || `org-${i}`).toLowerCase().replace(/\s+/g, "-")}`;
            return c.url
              ? (
                <a key={`org-${i}`} href={c.url} target="_blank" rel="noopener noreferrer" className="bento-card block hover:shadow-md transition-shadow" data-testid={testId}>
                  {inner}
                </a>
              )
              : (
                <div key={`org-${i}`} className="bento-card" data-testid={testId}>{inner}</div>
              );
          })}
        </div>
      </Section>

      <Section eyebrow={about.gallery?.eyebrow} title={about.gallery?.title} background>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {galleryToRender.map((src, i) => (
            <div key={`gallery-${i}-${src}`} className="aspect-square border overflow-hidden" style={{ borderColor: "var(--gi-border)" }} data-testid={`about-gallery-${i}`}>
              <img src={mediaUrl(src)} alt={`Author gallery image ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
        {about.gallery?.note && (
          <p className="mt-4 text-xs" style={{ color: "var(--gi-muted)" }} data-testid="about-gallery-note">{about.gallery.note}</p>
        )}
      </Section>

      <section className="container-wide py-28 text-center">
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{about.invite?.eyebrow}</div>
        <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }} data-testid="about-invite-title">{about.invite?.title}</h2>
        <p className="mt-6 max-w-2xl mx-auto text-lg" style={{ color: "var(--gi-charcoal-2)" }} data-testid="about-invite-body">
          {about.invite?.body}
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          {about.invite?.primary_cta_label && (
            <Link to={about.invite.primary_cta_to || "/contact"} className="btn-maroon" data-testid="about-invite-btn">
              {about.invite.primary_cta_label} <ArrowRight size={14} />
            </Link>
          )}
          {about.invite?.secondary_cta_label && (
            <Link to={about.invite.secondary_cta_to || "/join-the-movement"} className="btn-outline" data-testid="about-join-btn">
              {about.invite.secondary_cta_label}
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, eyebrow, children, background }) => (
  <section className="py-24" style={{ background: background ? "var(--gi-ivory-2)" : "transparent" }}>
    <div className="container-wide">
      {eyebrow && <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{eyebrow}</div>}
      {title && <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }}>{title}</h2>}
      <div className="hair-divider my-8 max-w-xs" />
      <div>{children}</div>
    </div>
  </section>
);
